import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  selectEnvironment,
  selectSelfCompanyDomain,
  selectSelfCompanyId,
  selectSelfLicenses,
  selectUserEmail,
} from 'containers/App/selectors'

const PORTAL_ID = '20641927'

export const DEEPFAKE_PROMO_TYPE = 'df2026.1'
export const Q4_2025_PROMO_TYPE = 'q42025.1'
export const ADVANCED_ATO_PROMO_TYPE = 'ato2026.1'
export const ENCRYPTION_PLG_PROMO_TYPE = 'encryption2026.1'
export const HRM_PLG_PROMO_TYPE = 'hrm2026.1'
export const MSP_ENCRYPTION_PROMO_TYPE = 'mspEncryption2026.1'

const ENCRYPTION_PLG_FORM_GUID = 'ce694c76-1ad2-4449-a3e5-9279a30f249d'
const MSP_ENCRYPTION_FORM_GUID = '037425d1-8fc4-4911-82b6-37d1a8546d0f'

const PROMOS = {
  [DEEPFAKE_PROMO_TYPE]: {
    pageUri: 'https://platform.ironscales.com/deepfake',
    pageName: 'Deepfake Protection PLG',
    formGuid: '9a598b02-f3c1-4629-b2c1-3a6fbd85bf79',
  },
  [Q4_2025_PROMO_TYPE]: {
    pageUri: 'https://platform.ironscales.com/q4promo',
    pageName: 'Q4 2025 Promo',
    formGuid: '08c4d80b-4c14-458c-89d7-32334e19288c',
  },
  [ADVANCED_ATO_PROMO_TYPE]: {
    pageUri: 'https://platform.ironscales.com/advanced-ato',
    pageName: 'Advanced Account Takeover Promo',
    formGuid: '9f638325-7e10-434c-ac8a-f5b8cbfc7595',
  },
  [ENCRYPTION_PLG_PROMO_TYPE]: {
    pageUri: 'https://platform.ironscales.com/encryption',
    pageName: 'Encryption Protection PLG',
    formGuid: ENCRYPTION_PLG_FORM_GUID,
  },
  [HRM_PLG_PROMO_TYPE]: {
    pageUri: 'https://platform.ironscales.com/hrm',
    pageName: 'HRM PLG',
    formGuid: 'f8e334f0-465d-4919-ac2a-e7a443bc217b',
  },
  [MSP_ENCRYPTION_PROMO_TYPE]: {
    pageUri: 'https://platform.ironscales.com/msp-encryption',
    pageName: 'MSP Elective Encryption - Free Sep-Nov 2026',
    formGuid: MSP_ENCRYPTION_FORM_GUID,
  },
}

const DEFAULT_PROMO_TYPE = Q4_2025_PROMO_TYPE

/**
 * @param {string} promoType - Key in PROMOS map
 * @returns {object} { submitPromo, isLoading, error, isSuccess }
 */
export const useHubSpotPromo = (promoType = DEFAULT_PROMO_TYPE) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const email = useSelector(selectUserEmail)
  const tenantId = useSelector(selectSelfCompanyId)
  const tenantDomain = useSelector(selectSelfCompanyDomain)
  const licenses = useSelector(selectSelfLicenses)
  const currentLicense = licenses?.plan
  const environment = useSelector(selectEnvironment)

  const submitPromo = useCallback(
    async (eventType) => {
      if (environment !== 'PROD') {
        return
      }

      const promo = PROMOS[promoType] || PROMOS[DEFAULT_PROMO_TYPE]

      const apiUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${promo.formGuid}`

      setIsLoading(true)
      setError(null)
      setIsSuccess(false)

      const payload = {
        fields: [
          {
            objectTypeId: '0-1',
            name: 'email',
            value: email,
          },
          {
            objectTypeId: '0-1',
            name: 'tenant_id_cnc',
            value: tenantId,
          },
          {
            objectTypeId: '0-1',
            name: 'tenant_domain_cnc',
            value: tenantDomain,
          },
          {
            objectTypeId: '0-1',
            name: 'current_license_cnc',
            value: currentLicense,
          },
          {
            objectTypeId: '0-1',
            name: 'event_type_cnc',
            value: eventType,
          },
        ],
        context: {
          pageUri: promo.pageUri,
          pageName: promo.pageName,
        },
      }

      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData?.message || response.statusText)
        }

        const data = await response.json()
        console.log('HubSpot form submission successful:', data)

        setIsSuccess(true)
        setIsLoading(false)

        return { success: true, data }
      } catch (err) {
        console.error('HubSpot form submission failed:', err)
        setError(err?.message || 'Submission failed')
        setIsLoading(false)

        return { success: false, error: err?.message }
      }
    },
    [email, tenantId, tenantDomain, currentLicense, environment, promoType]
  )

  const reset = useCallback(() => {
    setIsLoading(false)
    setError(null)
    setIsSuccess(false)
  }, [])

  return {
    submitPromo,
    isLoading,
    error,
    isSuccess,
    reset,
  }
}
